<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import ConversationProductsAPI from 'dashboard/api/conversationProducts';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  products: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['refresh']);

const isLoading = ref(false);
const showForm = ref(false);
const editingProduct = ref(null);
const formData = ref({
  name: '',
  description: '',
  amount: '',
  currency: 'BRL',
});

const currencyOptions = [
  { value: 'BRL', label: 'R$ (Real)' },
  { value: 'USD', label: '$ (Dólar)' },
  { value: 'EUR', label: '€ (Euro)' },
];

const totalByCurrency = computed(() => {
  const totals = {};
  props.products.forEach(product => {
    const curr = product.currency || 'BRL';
    if (!totals[curr]) totals[curr] = 0;
    totals[curr] += parseFloat(product.amount) || 0;
  });
  return totals;
});

const formatCurrency = (value, currency = 'BRL') => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(value || 0);
};

const formatDate = dateString => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const resetForm = () => {
  formData.value = { name: '', description: '', amount: '', currency: 'BRL' };
  editingProduct.value = null;
};

const openForm = (product = null) => {
  if (product) {
    editingProduct.value = product;
    formData.value = {
      name: product.name,
      description: product.description || '',
      amount: product.amount,
      currency: product.currency || 'BRL',
    };
  } else {
    resetForm();
  }
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const handleSave = async () => {
  if (!formData.value.name || !formData.value.amount) {
    useAlert('Preencha nome e valor do produto');
    return;
  }

  if (!props.conversationId) {
    useAlert('Selecione uma conversa para adicionar produtos');
    return;
  }

  isLoading.value = true;
  try {
    if (editingProduct.value) {
      await ConversationProductsAPI.update(
        props.conversationId,
        editingProduct.value.id,
        formData.value
      );
      useAlert('Produto atualizado!');
    } else {
      await ConversationProductsAPI.create(props.conversationId, formData.value);
      useAlert('Produto adicionado!');
    }
    closeForm();
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao salvar produto');
  } finally {
    isLoading.value = false;
  }
};

const handleDelete = async product => {
  if (!confirm('Excluir este produto?')) return;
  try {
    await ConversationProductsAPI.delete(
      props.conversationId || product.conversation_id,
      product.id
    );
    useAlert('Produto excluído!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao excluir produto');
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-medium text-n-slate-12">
          Produtos ({{ products.length }})
        </h3>
        <div
          v-if="Object.keys(totalByCurrency).length > 0"
          class="flex gap-3 mt-1"
        >
          <span
            v-for="(total, currency) in totalByCurrency"
            :key="currency"
            class="text-sm font-medium text-emerald-600 tabular-nums"
          >
            Total {{ currency }}: {{ formatCurrency(total, currency) }}
          </span>
        </div>
      </div>
      <Button
        label="Novo Produto"
        icon="i-lucide-plus"
        size="sm"
        :disabled="!conversationId"
        :title="!conversationId ? 'Selecione uma conversa primeiro' : ''"
        @click="openForm()"
      />
    </div>

    <!-- Empty State -->
    <div
      v-if="products.length === 0"
      class="flex flex-col items-center justify-center py-12 rounded-xl bg-n-alpha-1"
    >
      <span class="i-lucide-package size-12 text-n-slate-10 opacity-50" />
      <p class="mt-4 text-sm text-n-slate-11">Nenhum produto cadastrado</p>
    </div>

    <!-- Products List -->
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="product in products"
        :key="product.id"
        class="flex items-center justify-between p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-n-slate-12">{{ product.name }}</p>
          <p
            v-if="product.description"
            class="text-xs text-n-slate-11 mt-0.5 truncate"
          >
            {{ product.description }}
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            Adicionado em {{ formatDate(product.created_at) }}
          </p>
        </div>
        <div class="flex items-center gap-4 ml-4">
          <p class="text-base font-semibold text-emerald-600 tabular-nums">
            {{ formatCurrency(product.amount, product.currency) }}
          </p>
          <div class="flex items-center gap-1">
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="openForm(product)"
            >
              <span class="i-lucide-pencil size-4" />
            </button>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-ruby-600 hover:bg-ruby-500/10 transition-colors"
              @click="handleDelete(product)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Modal -->
    <Teleport to="body">
      <div
        v-if="showForm"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black5 backdrop-blur-sm"
          @click="closeForm"
        />
        <div
          class="relative w-full max-w-md overflow-hidden rounded-2xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          >
            <h3 class="text-base font-medium text-n-slate-12">
              {{ editingProduct ? 'Editar Produto' : 'Novo Produto' }}
            </h3>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="closeForm"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="p-6 flex flex-col gap-4">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Nome *</label>
              <input
                v-model="formData.name"
                type="text"
                placeholder="Nome do produto/serviço"
                class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
              />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-11">Valor *</label>
                <input
                  v-model="formData.amount"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-11">Moeda</label>
                <select
                  v-model="formData.currency"
                  class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                >
                  <option
                    v-for="opt in currencyOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Descrição</label>
              <textarea
                v-model="formData.description"
                rows="2"
                placeholder="Descrição opcional"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors resize-none"
              />
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
          >
            <Button
              label="Cancelar"
              variant="ghost"
              color="slate"
              @click="closeForm"
            />
            <Button
              :label="isLoading ? 'Salvando...' : 'Salvar'"
              :is-loading="isLoading"
              :disabled="isLoading"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
